/**
 * Time Utility Functions
 *
 * Reusable time formatting and calculation functions for the application.
 * Uses dayjs for date manipulation.
 */

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import { CATALOG_TIME_UNIT } from '@/constants'

// Initialize dayjs plugins
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(utc)

/**
 * Time unit value from CATALOG_TIME_UNIT constant
 */
export type TimeUnitValue = string

/**
 * Format time elapsed (past tense)
 * Returns single unit format: "2 days", "3 months", "12 hours", etc.
 *
 * @param date - Past date to calculate from
 * @returns Formatted string like "2 days", "3 months", or "12 hours"
 *
 * @example
 * formatTimeAgo('2024-01-01') // "3 months" (if today is April 2024)
 * formatTimeAgo('2024-04-20') // "10 days" (if today is April 30)
 * formatTimeAgo('2024-04-30T10:00:00') // "12 hours" (if now is 22:00 same day)
 */
export const formatTimeAgo = (date: string | Date): string => {
  const now = dayjs()
  const past = dayjs(date)
  const daysDiff = now.diff(past, 'days')

  // If less than 24 hours, show in hours
  if (daysDiff === 0) {
    const hoursDiff = now.diff(past, 'hours')
    return `${hoursDiff} ${hoursDiff === 1 ? 'hour' : 'hours'}`
  }

  // If more than 60 days (roughly 2 months), show in months
  if (daysDiff >= 60) {
    const months = Math.floor(daysDiff / 30)
    return `${months} ${months === 1 ? 'month' : 'months'}`
  }

  // Otherwise show in days
  return `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`
}

/**
 * Format time remaining (future tense)
 * Returns single unit format: "12 hs", "3 days", etc.
 * Uses hours for < 24 hours, days for >= 24 hours
 *
 * @param date - Future date to calculate to
 * @returns Formatted string like "12 hs" or "3 days"
 *
 * @example
 * formatTimeRemaining(futureDate) // "12 hs" (if 12 hours away)
 * formatTimeRemaining(futureDate) // "3 days" (if 3 days away)
 */
export const formatTimeRemaining = (date: string | Date): string => {
  const now = dayjs()
  const future = dayjs(date)
  const hoursDiff = future.diff(now, 'hours')

  // If less than 24 hours, show in hours
  if (hoursDiff < 24) {
    return `${hoursDiff} hs`
  }

  // Otherwise show in days
  const daysDiff = Math.floor(hoursDiff / 24)
  return `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`
}

/**
 * Calculate expiration date from active timestamp and time limit
 *
 * @param activeTs - Activation timestamp (PostgreSQL timestamp format or ISO string)
 * @param limit - Time limit value (e.g., 48 for 48 hours)
 * @param unit - Time unit UUID from CATALOG_TIME_UNIT
 * @returns Expiration date
 *
 * @example
 * calculateExpiration('2024-01-01T12:00:00Z', 48, CATALOG_TIME_UNIT.HOURS) // 2 days later
 * calculateExpiration('2025-11-14 20:55:01.621+00', 7, CATALOG_TIME_UNIT.DAYS) // 7 days later
 */
export const calculateExpiration = (
  activeTs: string,
  limit: number,
  unit: TimeUnitValue
): Date => {
  const active = dayjs(activeTs)

  // Match against catalog UUID values
  switch (unit) {
    case CATALOG_TIME_UNIT.HOURS:
      return active.add(limit, 'hours').toDate()
    case CATALOG_TIME_UNIT.DAYS:
      return active.add(limit, 'days').toDate()
    case CATALOG_TIME_UNIT.WEEKS:
      return active.add(limit, 'weeks').toDate()
    case CATALOG_TIME_UNIT.MONTHS:
      return active.add(limit, 'months').toDate()
    default:
      // Fallback: If unit is not a recognized UUID, try string matching for backwards compatibility
      if (unit.toLowerCase().includes('hour')) {
        return active.add(limit, 'hours').toDate()
      }
      if (unit.toLowerCase().includes('week')) {
        return active.add(limit, 'weeks').toDate()
      }
      if (unit.toLowerCase().includes('month')) {
        return active.add(limit, 'months').toDate()
      }
      // Default to days
      return active.add(limit, 'days').toDate()
  }
}

/**
 * Get number of days between two dates
 *
 * @param start - Start date
 * @param end - End date
 * @returns Number of days (can be negative if end is before start)
 */
export const getDaysBetween = (
  start: Date | string,
  end: Date | string
): number => dayjs(end).diff(dayjs(start), 'days')

/**
 * Get number of hours between two dates
 *
 * @param start - Start date
 * @param end - End date
 * @returns Number of hours (can be negative if end is before start)
 */
export const getHoursBetween = (
  start: Date | string,
  end: Date | string
): number => dayjs(end).diff(dayjs(start), 'hours')

/**
 * Format days value to "X days" with proper singular/plural
 *
 * @param days - Number of days
 * @returns Formatted string like "1 day" or "5 days"
 */
export const formatDays = (days: number): string =>
  `${days} ${days === 1 ? 'day' : 'days'}`

/**
 * Check if a date has passed (is in the past)
 *
 * @param date - Date to check
 * @returns true if date is in the past
 */
export const isPast = (date: string | Date): boolean =>
  dayjs(date).isBefore(dayjs())
